import { BUTTON_LABELS, COMMON_TEXT } from '@/constants';
import { Button, ButtonProps } from '@/components/Buttons';
import { TIconName } from '@/design-tokens';
import { usePrintFeature } from '@/features/print/usePrintFeature';
import { UsePrintFeatureParams } from '@/features/print/types';

/** Props for the PrintButton component. */
interface PrintButtonProps {
  /** The icon to display on the button. */
  buttonIcon?: TIconName;
  /** The label text for the button. */
  buttonLabel?: string;
  /** The size of the button. */
  buttonSize?: ButtonProps['size'];
  /** The variant of the button. */
  buttonVariant?: ButtonProps['variant'];
  /** The ref for the button element. */
  contentRef: UsePrintFeatureParams['contentRef'];
  /** The title to use for the print document. */
  documentTitle?: UsePrintFeatureParams['documentTitle'] | string;
  /** The custom page style for the print document. */
  pageStyle?: UsePrintFeatureParams['pageStyle'] | string;
  /** The click handler for the button. */
  onClick?: () => void;
}

/**
 * A button component for printing, which can be used in various parts of the application
 * where a print action is needed. It accepts customizable props for label, icon, size,
 * and variant to fit different use cases.
 */
export const PrintButton: React.FC<PrintButtonProps> = ({
  buttonIcon = 'Download',
  buttonLabel = BUTTON_LABELS.PDF_PRINT,
  buttonSize = 'small',
  buttonVariant = 'primary',
  contentRef,
  documentTitle = COMMON_TEXT.PRINT_DOCUMENT,
  pageStyle = `@page { size: A2; margin: 20mm; }`,
  onClick,
}) => {
  // Use the custom usePrintFeature hook to get the print handler and printing state
  const { handlePrint, isPrinting } = usePrintFeature({
    contentRef,
    documentTitle,
    pageStyle,
    onClick,
   });

  // Render the Button component with the provided props and the click handler
  return (
    <Button
      className="print:hidden"
      disabled={isPrinting}
      iconRight={buttonIcon}
      label={buttonLabel}
      onClick={handlePrint}
      size={buttonSize}
      variant={buttonVariant}
    />
  );
};

PrintButton.displayName = 'PrintButton';

export default PrintButton;
